import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Calendar, ChevronRight, MapPin } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MeetingDetailModal } from '@/components/modals/MeetingDetailModal';
import { formatDealMeetingRange, type DealTimelineMeeting } from './dealMeetingTimeline';

interface DealMeeting extends DealTimelineMeeting {
  location?: string | null;
  contactName?: string | null;
  companyName?: string | null;
  eventTitle?: string | null;
  participants?: Array<{ displayName?: string | null; contactName?: string | null; email?: string | null }>;
}

interface DealMeetingsPanelProps {
  dealId: number;
  className?: string;
}

function meetingReferences(meeting: DealMeeting): string[] {
  const participantNames = (meeting.participants ?? [])
    .map((participant) => participant.displayName || participant.contactName || participant.email)
    .filter((name): name is string => Boolean(name));
  return Array.from(new Set([
    ...participantNames,
    meeting.contactName,
    meeting.companyName,
    meeting.eventTitle,
  ].filter((value): value is string => Boolean(value))));
}

/** Reader unico degli appuntamenti gia associati al Deal, per scheda e drawer. */
export function DealMeetingsPanel({ dealId, className = '' }: DealMeetingsPanelProps) {
  const queryClient = useQueryClient();
  const [selectedMeetingId, setSelectedMeetingId] = useState<number | null>(null);
  const queryKey = ['/api/meetings', { dealId }] as const;
  const { data: meetings = [], isLoading, isError, refetch } = useQuery<DealMeeting[]>({
    queryKey,
    queryFn: () => apiRequest(`/api/meetings?dealId=${dealId}`),
    enabled: Number.isSafeInteger(dealId) && dealId > 0,
  });

  return (
    <>
      <Card className={`rounded-none ${className}`} data-testid="deal-meetings-panel">
        <CardHeader className="border-b border-border px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Calendar className="h-4 w-4 text-primary" />
              Appuntamenti e calendario
            </CardTitle>
            {!isLoading && !isError && <Badge variant="outline" className="rounded-none">{meetings.length}</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">Meeting collegati a questa opportunità, con data, riferimenti e dettaglio apribile.</p>
        </CardHeader>
        <CardContent className="px-4 py-4">
          {isLoading ? (
            <div className="space-y-2" aria-label="Caricamento appuntamenti">
              <Skeleton className="h-16 w-full rounded-none" />
              <Skeleton className="h-16 w-full rounded-none" />
            </div>
          ) : isError ? (
            <div role="alert" className="space-y-3 text-sm text-muted-foreground">
              <p className="flex items-start gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />Non è stato possibile leggere gli appuntamenti del Deal.</p>
              <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>Riprova</Button>
            </div>
          ) : meetings.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun appuntamento collegato a questo Deal.</p>
          ) : (
            <div className="divide-y divide-border border-y border-border">
              {meetings.map((meeting) => {
                const references = meetingReferences(meeting);
                return (
                  <button
                    key={meeting.id}
                    type="button"
                    className="flex min-h-11 w-full min-w-0 items-start gap-3 py-3 text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => setSelectedMeetingId(meeting.id)}
                    aria-label={`Apri appuntamento ${meeting.title}`}
                  >
                    <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                      <span className="block break-words text-sm font-medium text-foreground">{meeting.title}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {[meeting.meetingType, formatDealMeetingRange(meeting)].filter(Boolean).join(' · ')}
                      </span>
                      {references.length > 0 && <span className="mt-1 block break-words text-xs text-muted-foreground">{references.join(' · ')}</span>}
                      {meeting.location && <span className="mt-1 flex items-start gap-1 break-words text-xs text-muted-foreground"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{meeting.location}</span>}
                    </span>
                    <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      <MeetingDetailModal
        meetingId={selectedMeetingId}
        isOpen={selectedMeetingId !== null}
        onOpenChange={(open) => { if (!open) setSelectedMeetingId(null); }}
        onSuccess={() => queryClient.invalidateQueries({ queryKey })}
      />
    </>
  );
}
